import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { serviceApi } from "@/lib/sprint2Entities";
import { assetApi } from "@/lib/entities";
import { paginatedFetch, wrapListApi } from "@/lib/paginatedFetch";
import { apiError } from "@/lib/errors";
import { PageHeader, PageShell } from "@/components/PageHeader";
import { Wrench, AlertOctagon } from "lucide-react";
import { assetDisplay } from "@/lib/assetDisplay";
import { ActionMenu } from "@/components/ActionMenu";
import { api } from "@/lib/api";

const STATUSES = ["open", "diagnosing", "waiting_parts", "in_progress", "completed"];
const PRIORITIES = ["low", "normal", "high", "urgent"];

const PRIORITY_COLORS = {
  low: "text-steel border-steel",
  normal: "text-coach-blue border-coach-blue",
  high: "text-amber-hold border-amber-hold",
  urgent: "text-critical-red border-critical-red",
};

export default function ServicePage() {
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState({
    asset_id: "", issue: "", priority: "normal", vendor: "", technician: "",
  });
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    try {
      setRows(await serviceApi.list(statusFilter ? { status: statusFilter } : {}));
      // RC10p — paginate instead of asking for 200 (now 422).
      const a = await paginatedFetch(wrapListApi(assetApi.list), { pageSize: 100 });
      setAssets(a.items.filter((x) => x.asset_type !== "trailer"));
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  const create = async () => {
    if (!form.asset_id || !form.issue) return toast.error("Asset and issue required");
    try {
      await serviceApi.create(form);
      setForm({ asset_id: "", issue: "", priority: "normal", vendor: "", technician: "" });
      toast.success("Work order opened · asset moved to Maintenance");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (id, s) => {
    try { await serviceApi.patch(id, { status: s, ...(s === "completed" ? { completed_date: new Date().toISOString().slice(0, 10) } : {}) }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <PageShell>
      <PageHeader
        testid="page-service"
        title={<span className="inline-flex items-center gap-2"><Wrench size={22} className="text-coach-blue" />Service Department</span>}
        subtitle="Work orders, vendor coordination, and coach service tracking"
      />

      <div className="bg-white border border-coach-silver rounded-md p-4 mb-4" data-testid="service-create">
        <div className="text-[10.5px] uppercase tracking-widest text-steel mb-2">Open work order</div>
        <div className="grid md:grid-cols-6 gap-2 items-end">
          <label className="block md:col-span-2">
            <div className="text-[10.5px] text-steel mb-1">Coach</div>
            <select value={form.asset_id} onChange={(e) => setForm({ ...form, asset_id: e.target.value })}
              className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px] bg-white"
              data-testid="service-asset">
              <option value="">— Choose —</option>
              {assets.map((a) => <option key={a.id} value={a.id}>{assetDisplay(a)}</option>)}
            </select>
          </label>
          <label className="block md:col-span-2">
            <div className="text-[10.5px] text-steel mb-1">Issue</div>
            <input value={form.issue} onChange={(e) => setForm({ ...form, issue: e.target.value })}
              className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px]"
              data-testid="service-issue" />
          </label>
          <label className="block">
            <div className="text-[10.5px] text-steel mb-1">Priority</div>
            <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
              className="w-full border border-coach-silver rounded-sm px-2 py-1.5 text-[13px] bg-white"
              data-testid="service-priority">
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </label>
          <button onClick={create} className="bg-coach-blue text-white text-[12.5px] font-medium px-3 py-2 rounded-sm"
            data-testid="service-open">Open WO</button>
        </div>
        <div className="text-[11px] text-steel mt-2">
          <AlertOctagon size={11} className="inline mr-1 text-amber-hold" />
          Opening a work order (non-completed) forces the coach's operational status to <strong>maintenance</strong>.
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <div className="text-[11px] uppercase tracking-widest text-steel">Filter:</div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-coach-silver rounded-sm px-2 py-1 text-[12.5px] bg-white"
          data-testid="service-filter">
          <option value="">All</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
        </select>
      </div>

      <div className="bg-white border border-coach-silver rounded-md">
        <table className="w-full text-[13px]">
          <thead className="bg-road-white text-[10.5px] uppercase tracking-widest text-steel">
            <tr>
              <th className="text-left px-3 py-2 w-28">WO #</th>
              <th className="text-left px-3 py-2 w-40">Coach</th>
              <th className="text-left px-3 py-2">Issue</th>
              <th className="text-left px-3 py-2 w-24">Priority</th>
              <th className="text-left px-3 py-2 w-32">Status</th>
              <th className="text-left px-3 py-2 w-40">Vendor / Tech</th>
              <th className="w-52" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const asset = assets.find((a) => a.id === r.asset_id);
              return (
                <tr key={r.id} className="border-t border-coach-silver/60" data-testid={`wo-row-${r.id}`}>
                  <td className="px-3 py-2 text-carbon font-mono">{r.work_order_number}</td>
                  <td className="px-3 py-2 text-carbon">{assetDisplay(asset) || r.asset_id.slice(0, 8)}</td>
                  <td className="px-3 py-2 text-carbon">{r.issue}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 border rounded-sm text-[10.5px] uppercase tracking-widest ${PRIORITY_COLORS[r.priority] || ""}`}>{r.priority}</span>
                  </td>
                  <td className="px-3 py-2 text-steel">{r.status?.replaceAll("_", " ")}</td>
                  <td className="px-3 py-2 text-steel">{[r.vendor, r.technician].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex items-center gap-1">
                      <select onChange={(e) => e.target.value && setStatus(r.id, e.target.value)} defaultValue=""
                        className="border border-coach-silver rounded-sm px-1.5 py-0.5 text-[12px] bg-white"
                        data-testid={`wo-status-${r.id}`}>
                        <option value="">Change status…</option>
                        {STATUSES.filter((s) => s !== r.status).map((s) => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}
                      </select>
                      <ActionMenu
                        testid={`wo-actions-${r.id}`}
                        onArchive={
                          // Archive is the safe disposition. Completed/closed
                          // WOs skip Edit entirely; only Archive remains.
                          async () => {
                            if (!window.confirm(`Archive WO ${r.work_order_number}?`)) return;
                            try {
                              await api.post(`/service/work-orders/${r.id}/archive`);
                              toast.success(`Archived ${r.work_order_number}`);
                              load();
                            } catch (e) { toast.error(e?.response?.data?.detail || "Archive failed"); }
                          }
                        }
                        archiveLabel="Archive"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-steel">No work orders.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </PageShell>
  );
}
